import type { TimeBlock } from '../types';

/**
 * Day-relative scheduling helpers for the template editor.
 *
 * A template has a `dayStart` (wall-clock HH:MM, e.g. wake time). Ordering, overlap
 * detection and gaps are all computed in "minutes since dayStart" so a day that
 * runs 16:00 → 16:00 next day (or a block that crosses midnight) behaves correctly.
 */

export const DAY_MIN = 1440;
export const DEFAULT_DAY_START = '00:00';

export const toMin = (t: string): number => {
  const [h, m] = (t || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

export const minToHHMM = (min: number): string => {
  const norm = ((min % DAY_MIN) + DAY_MIN) % DAY_MIN;
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

/** Block duration in minutes, wrapping past midnight (e.g. 23:30→00:30 = 60). */
export const blockDurationMin = (b: { startTime: string; endTime: string }): number => {
  let d = toMin(b.endTime) - toMin(b.startTime);
  if (d < 0) d += DAY_MIN;
  return d;
};

/** Minutes from dayStart to this block's start (0..1439). */
export const dayRelStart = (b: { startTime: string }, dayStart: string): number =>
  (((toMin(b.startTime) - toMin(dayStart)) % DAY_MIN) + DAY_MIN) % DAY_MIN;

/** Sort blocks by their position in the day (relative to dayStart), then by duration. */
export function sortBlocksByDay<T extends TimeBlock>(blocks: T[], dayStart: string): T[] {
  return [...blocks].sort(
    (a, b) => dayRelStart(a, dayStart) - dayRelStart(b, dayStart) || blockDurationMin(a) - blockDurationMin(b)
  );
}

// Linear interval intersection [s1,e1) ∩ [s2,e2).
const intersects = (s1: number, e1: number, s2: number, e2: number) => s1 < e2 && s2 < e1;

/** Ids of blocks that overlap at least one other block (day-relative, wrap-aware). */
export function overlappingBlockIds(blocks: TimeBlock[], dayStart: string): Set<string> {
  const out = new Set<string>();
  const arr = blocks.map((b) => {
    const s = dayRelStart(b, dayStart);
    return { id: b.id, s, e: s + blockDurationMin(b) };
  });
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      const a = arr[i];
      const b = arr[j];
      if (a.e === a.s || b.e === b.s) continue; // zero-length can't overlap
      // test b against a, and b shifted ±a full day to catch wrap across the day boundary
      const hit =
        intersects(a.s, a.e, b.s, b.e) ||
        intersects(a.s, a.e, b.s + DAY_MIN, b.e + DAY_MIN) ||
        intersects(a.s, a.e, b.s - DAY_MIN, b.e - DAY_MIN);
      if (hit) {
        out.add(a.id);
        out.add(b.id);
      }
    }
  }
  return out;
}

/**
 * Ids of blocks with no length at all — start equals end.
 *
 * ⚠️⚠️ SUCH A BLOCK CAN NEVER BE REACHED. `findCurrentAndNextBlocks` asks
 * `now >= start && now < end`, which is unsatisfiable when the two are equal, so
 * the block can never become the current one and therefore never opens in Focus.
 * It occupies no time either — `freeGaps` hands its minutes straight to the
 * neighbouring gap — so nothing on the day view looks wrong. It is invisible in
 * every direction except by measuring it, which is why one sat in a real
 * template (`Personal 01:00–01:00`) until Focus mode was rebuilt around it.
 *
 * ⚠️ `overlappingBlockIds` already SKIPS these (a zero-length span cannot
 * intersect anything), so the two flags are mutually exclusive by construction —
 * a malformed block would otherwise be silently exempt from the only check the
 * editor ran on times.
 *
 * ⚠️ Give this the EDITABLE blocks, not `layoutBlocks`: generated prayer spans
 * and the sleep pseudo-block cannot be fixed in the editor, so flagging one
 * would be a dead end. (Neither can be zero-length today — `sleepSpan` returns
 * null at zero — but the rule is about who can act on it, not who trips it.)
 */
export function zeroLengthBlockIds(blocks: TimeBlock[]): Set<string> {
  return new Set(blocks.filter((b) => blockDurationMin(b) === 0).map((b) => b.id));
}

/**
 * Insert `newBlock` and push any blocks it now overlaps later (cascading), preserving
 * each pushed block's duration. Blocks separated by free time are untouched — the gap
 * absorbs the shift. Returns a new day-sorted array. Lets you insert between back-to-back
 * blocks: the following block(s) move to start right after the inserted one.
 */
export function insertWithPush(blocks: TimeBlock[], dayStart: string, newBlock: TimeBlock): TimeBlock[] {
  const sorted = sortBlocksByDay([...blocks, newBlock], dayStart).map((b) => ({ ...b }));
  const idx = sorted.findIndex((b) => b.id === newBlock.id);
  if (idx < 0) return sorted;
  let cursorRel = dayRelStart(newBlock, dayStart) + blockDurationMin(newBlock);
  for (let i = idx + 1; i < sorted.length; i++) {
    const relStart = dayRelStart(sorted[i], dayStart);
    if (relStart < cursorRel - 1e-9) {
      const dur = blockDurationMin(sorted[i]);
      const startMin = toMin(dayStart) + cursorRel;
      sorted[i] = { ...sorted[i], startTime: minToHHMM(startMin), endTime: minToHHMM(startMin + dur) };
      cursorRel += dur;
    } else {
      break; // a gap absorbs the shift — everything after is already clear
    }
  }
  return sorted;
}

export interface SleepSpan {
  startTime: string; // bedtime (HH:MM)
  endTime: string; // wake = dayStart (HH:MM)
  minutes: number;
}

/**
 * Sleep span for a template: from `bedtime` to `dayStart` (wake), as the closing
 * block of the day. Returns null when no bedtime is set or it resolves to 0 length.
 */
export function sleepSpan(dayStart: string, bedtime?: string | null): SleepSpan | null {
  if (!bedtime) return null;
  const minutes = (((toMin(dayStart) - toMin(bedtime)) % DAY_MIN) + DAY_MIN) % DAY_MIN;
  if (minutes === 0) return null;
  return { startTime: bedtime, endTime: dayStart, minutes };
}

/** A non-editable pseudo-block representing sleep, for overlap/gap/clock computation. */
export function sleepPseudoBlock(sleep: SleepSpan): TimeBlock {
  return {
    id: '__sleep__',
    name: 'Sleep',
    startTime: sleep.startTime,
    endTime: sleep.endTime,
    category: '',
    color: '',
    type: 'time',
  } as TimeBlock;
}

export const SLEEP_BLOCK_ID = '__sleep__';

export interface DayGap {
  /** Wall-clock time (HH:MM) the free gap begins. */
  startTime: string;
  /** Free minutes in the gap. */
  minutes: number;
  /** Insertion index into the day-sorted block array. */
  position: number;
}

/**
 * Free (unscheduled) gaps across the day, in day order: a leading gap from dayStart
 * to the first block, gaps between consecutive blocks, and a trailing gap to the end
 * of the day window. Only positive gaps are returned. Assumes `sorted` is already
 * day-sorted. Overlaps produce negative spans and are omitted (handled separately).
 */
export function freeGaps(sorted: TimeBlock[], dayStart: string): DayGap[] {
  const gaps: DayGap[] = [];
  const n = sorted.length;
  if (n === 0) {
    return [{ startTime: dayStart, minutes: DAY_MIN, position: 0 }];
  }

  const lead = dayRelStart(sorted[0], dayStart);
  if (lead > 0) gaps.push({ startTime: dayStart, minutes: lead, position: 0 });

  for (let i = 0; i < n - 1; i++) {
    const relEnd = dayRelStart(sorted[i], dayStart) + blockDurationMin(sorted[i]);
    const gap = dayRelStart(sorted[i + 1], dayStart) - relEnd;
    if (gap > 0) gaps.push({ startTime: sorted[i].endTime, minutes: gap, position: i + 1 });
  }

  const relEndLast = dayRelStart(sorted[n - 1], dayStart) + blockDurationMin(sorted[n - 1]);
  const trail = DAY_MIN - relEndLast;
  if (trail > 0) gaps.push({ startTime: sorted[n - 1].endTime, minutes: trail, position: n });

  return gaps;
}
